import { useMenuContext } from "Components";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { UserMenu } from "./Components";
import Logo from "svg/Logo";
import { SvgMenuLarge } from "melodies-source/Svgs/MenuLarge";
import { SvgMenuOpenLarge } from "melodies-source/Svgs/MenuOpenLarge";
import { SvgHelpAlt } from "melodies-source/Svgs/HelpAlt";
import { useCustomAppContext } from "contexts/CustomAppContext";
import { Subtitle1 } from "melodies-source/Text";
import { ReactComponent as SetAdminLogo } from "assets/svg/set-admin-white.svg";

interface Props {
  colorMode?: "light" | "dark";
}

export const MenuBar: React.FC<Props> = ({ colorMode = "light" }) => {
  const { menuMode, setMenuMode } = useMenuContext();
  const history = useHistory();
  const { artistId } = useParams<{ artistId: string }>();
  const { customApp } = useCustomAppContext();
  const location = useLocation();
  const isSetAdmin = location.pathname.startsWith("/admin");

  return (
    <Container id="menu-bar" data-color-mode={colorMode}>
      <Left>
        {location.pathname !== "/" && (
          <MenuIcon
            as={menuMode === "expanded" ? SvgMenuOpenLarge : SvgMenuLarge}
            onClick={() =>
              setMenuMode((menuMode) =>
                menuMode === "expanded" ? "hidden" : "expanded",
              )
            }
          />
        )}
        <LogoContainer>
          {isSetAdmin ? (
            <LogoLink to="/admin">
              <StyledSetAdminLogo />
            </LogoLink>
          ) : (
            <LogoLink to="/">
              <StyledLogo isCustom={!!customApp} />
            </LogoLink>
          )}
          {customApp?.app.name && (
            <>
              <LogoDivider />
              <Subtitle1>{customApp.app.name}</Subtitle1>
            </>
          )}
        </LogoContainer>
      </Left>
      <Right>
        {artistId && !customApp && (
          <StyledHelpIcon
            onClick={() => history.push(`/${artistId}/help-center`)}
          />
        )}
        <UserMenu artistId={artistId} isSetAdmin={isSetAdmin} />
      </Right>
    </Container>
  );
};

const Container = styled.div`
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: inset 0 -1px 0 0 var(--box-shadow-color);
  background: white;
  z-index: 10;

  &[data-color-mode="dark"] {
    background: black;
  }

  ${({ theme }) => theme.media.mobile} {
    padding: 12px;
    height: 39px;
    position: fixed;
    top: 0;
    z-index: 16;
    width: 100%;
  }

  ${({ theme }) => {
    if (!theme.custom) return null;
    const { background } = theme.custom.layout.menuBar;
    return css`
      background: ${background};
    `;
  }}
`;

const MenuIcon = styled.svg`
  margin: 0 12px 0 0;
  cursor: pointer;
  user-select: none;

  ${({ theme }) => theme.mediaQueries.desktop} {
    display: none;
  }

  [data-color-mode="dark"] & {
    color: white;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
`;

const StyledHelpIcon = styled(SvgHelpAlt)`
  color: #666666;

  &:hover {
    cursor: pointer;
  }

  ${({ theme }) => {
    if (!theme.custom) return null;
    const { color } = theme.custom.layout.menuBar;
    return css`
      color: ${color};
    `;
  }}
`;

interface StyledLogoProps {
  isCustom: boolean;
}

const logoStyles = css`
  cursor: pointer;
  height: 44px;
  width: auto;

  ${({ theme }) => theme.media.mobile} {
    height: 19px;
  }
`;

const StyledLogo = styled(Logo)<StyledLogoProps>`
  ${logoStyles}

  ${({ isCustom }) =>
    isCustom &&
    css`
      height: 35px;
      margin-left: 5px;
    `}
`;

const StyledSetAdminLogo = styled(SetAdminLogo)`
  ${logoStyles}
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const LogoDivider = styled.div`
  width: 2px;
  height: 13px;
  background: ${({ theme }) => theme.custom.colors.main};
`;
